/**
 * PTEL Visor - MapExport Component
 * Exportar vista actual del mapa como imagen PNG o documento PDF
 *
 * F8.06 - Usa MapExportService para captura y generación
 * Soporte: PNG (imagen directa) + PDF (con metadatos municipio)
 */

import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import {
  MapExportService,
  type MapExportMetadata,
  type PdfExportOptions,
} from '../../services/export/MapExportService';
// SSCA-FIX-2 (SF2B): guard de montaje para setState tras await
import { useIsMounted } from '@/hooks/useIsMounted';

export type ExportFormat = 'png' | 'pdf';
export type ExportStatus = 'idle' | 'capturing' | 'success' | 'error';

export interface MapExportProps {
  /** Ref al contenedor del mapa (div que envuelve LeafletMapContainer) */
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Metadatos del municipio (requerido para cabecera PDF) */
  metadata: MapExportMetadata;
  /** Nombre base del archivo (sin extensión) */
  filename?: string;
  /** Opciones PDF (orientación, tamaño, márgenes) */
  pdfOptions?: PdfExportOptions;
  /** Clase CSS adicional */
  className?: string;
  /** Deshabilitar botón */
  disabled?: boolean;
  /** Callback al iniciar exportación */
  onExportStart?: (format: ExportFormat) => void;
  /** Callback al completar exportación */
  onExportComplete?: (success: boolean, format: ExportFormat) => void;
}

export const MapExport = memo<MapExportProps>(function MapExport({
  mapContainerRef,
  metadata,
  filename = 'mapa-ptel',
  pdfOptions,
  className = '',
  disabled = false,
  onExportStart,
  onExportComplete,
}) {
  const [status, setStatus] = useState<ExportStatus>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [format, setFormat] = useState<ExportFormat>('png');

  // SSCA-FIX-2 (SF2B): guard de montaje uniforme para el setState async.
  const isMounted = useIsMounted();
  // SF2B (DOP-SF2B-TIMER): ref del temporizador de reset a «idle» para
  // cancelarlo al desmontar (cleanup en el useEffect de abajo).
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SF2B (DOP-SF2B-TIMER): cancelar el temporizador de reset al desmontar.
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleExport = useCallback(async () => {
    if (!mapContainerRef.current || disabled) return;

    setStatus('capturing');
    setErrorMsg(null);
    onExportStart?.(format);

    try {
      const result = await MapExportService.exportAndDownload(
        {
          mapContainer: mapContainerRef.current,
          filename,
          metadata,
        },
        format,
        format === 'pdf' ? pdfOptions : undefined
      );

      // SSCA-FIX-2 (SF2B): la exportación es async; si el componente se
      // desmontó mientras se resolvía, no agendar setState ni notificar.
      if (!isMounted()) return;

      if (result.success) {
        setStatus('success');
        onExportComplete?.(true, format);
      } else {
        setErrorMsg(result.error ?? 'Error desconocido');
        setStatus('error');
        onExportComplete?.(false, format);
      }

      // SF2B (DOP-SF2B-TIMER): el temporizador de reset a «idle» se guarda en
      // una ref para cancelarlo en el cleanup de useEffect — antes quedaba
      // huérfano y podía disparar setStatus tras el desmontaje.
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setStatus('idle'), 2500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      if (isMounted()) {
        setErrorMsg(message);
        setStatus('error');
        onExportComplete?.(false, format);
      }
    }
  }, [mapContainerRef, filename, metadata, format, pdfOptions, disabled, onExportStart, onExportComplete, isMounted]);

  const isCapturing = status === 'capturing';

  const statusIcon: Record<ExportStatus, string> = {
    idle: '📷',
    capturing: '⏳',
    success: '✅',
    error: '❌',
  };

  const statusLabel: Record<ExportStatus, string> = {
    idle: `Exportar ${format.toUpperCase()}`,
    capturing: 'Exportando...',
    success: 'Descargado',
    error: 'Error',
  };

  return (
    <div className={`map-export ${className}`} data-testid="map-export">
      {/* Selector de formato */}
      <div className="map-export__format" data-testid="map-export-format">
        <button
          type="button"
          onClick={() => setFormat('png')}
          disabled={isCapturing}
          aria-pressed={format === 'png'}
          className={`map-export__format-btn ${format === 'png' ? 'map-export__format-btn--active' : ''}`}
          style={{
            padding: '2px 8px',
            fontSize: '0.75rem',
            fontFamily: "'JetBrains Mono', monospace",
            border: '1px solid',
            borderColor: format === 'png' ? '#A3E635' : '#555',
            background: format === 'png' ? 'rgba(163, 230, 53, 0.15)' : 'transparent',
            color: format === 'png' ? '#A3E635' : '#999',
            borderRadius: '3px 0 0 3px',
            cursor: isCapturing ? 'not-allowed' : 'pointer',
          }}
        >
          PNG
        </button>
        <button
          type="button"
          onClick={() => setFormat('pdf')}
          disabled={isCapturing}
          aria-pressed={format === 'pdf'}
          className={`map-export__format-btn ${format === 'pdf' ? 'map-export__format-btn--active' : ''}`}
          style={{
            padding: '2px 8px',
            fontSize: '0.75rem',
            fontFamily: "'JetBrains Mono', monospace",
            border: '1px solid',
            borderColor: format === 'pdf' ? '#A3E635' : '#555',
            background: format === 'pdf' ? 'rgba(163, 230, 53, 0.15)' : 'transparent',
            color: format === 'pdf' ? '#A3E635' : '#999',
            borderRadius: '0 3px 3px 0',
            borderLeft: 'none',
            cursor: isCapturing ? 'not-allowed' : 'pointer',
          }}
        >
          PDF
        </button>
      </div>

      {/* Botón de exportación */}
      <button
        type="button"
        onClick={handleExport}
        disabled={disabled || isCapturing}
        aria-busy={isCapturing}
        aria-label={isCapturing ? 'Exportando mapa...' : `Exportar mapa como ${format.toUpperCase()}`}
        className="map-export__button"
        data-testid="map-export-button"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginTop: '4px',
          cursor: disabled || isCapturing ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          fontFamily: "'Space Grotesk', system-ui, sans-serif",
        }}
      >
        <span aria-hidden="true">{statusIcon[status]}</span>
        <span>{statusLabel[status]}</span>
      </button>

      {/* Mensaje de error */}
      {status === 'error' && errorMsg && (
        <p
          role="alert"
          data-testid="map-export-error"
          style={{ color: '#dc2626', fontSize: '0.75rem', margin: '4px 0 0' }}
        >
          {errorMsg}
        </p>
      )}
    </div>
  );
});

export default MapExport;
